import React, { useState, useEffect, useMemo } from "react";
import { EntityTablePage, SaveConfirmationConfig, DeleteConfirmationConfig } from "@/components/EntityTablePage";
import { getUserTableConfig, MarketOption } from "@/config/tableConfigs/userTableConfig";
import { userService } from "@/services/userService";
import useAuth from "@/hooks/useAuth";
import { NoPermissionScreen } from "@/components/screens/shared/NoPermissionScreen";
import type { UserType } from "@/types/db/users";
import { Link } from "expo-router";
import { supabase } from "@/lib/supabase-client";
import { DeactivateUserModal } from "@/components/Modals/DeactivateUserModal";
import { DeleteUserModal } from "@/components/Modals/DeleteUserModal";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useSetPageTitle } from "@/hooks/useSetPageTitle";

export default function UsersList() {
  const { userRoles, displayNames } = useAuth();
  const [marketOptions, setMarketOptions] = useState<MarketOption[]>([]);
  useSetPageTitle(displayNames?.users?.plural);

  // Fetch markets on mount
  useEffect(() => {
    (async () => {
      const { data } = await supabase
        .schema("lists")
        .from("markets")
        .select("id, name")
        .order("name", { ascending: true });

      if (data) {
        setMarketOptions(data.map((m) => ({ value: m.id, label: m.name })));
      }
    })();
  }, []);

  const tableConfig = useMemo(
    () => getUserTableConfig(userRoles, marketOptions, displayNames),
    [userRoles, marketOptions, displayNames],
  );

  // Check if user has users read permission
  const hasUsersAccess = useMemo(() => {
    return userRoles?.some((r) => r.permissions?.users?.read === true) ?? false;
  }, [userRoles]);

  // Save confirmation for deactivating users
  const saveConfirmation: SaveConfirmationConfig<UserType> = useMemo(
    () => ({
      shouldConfirm: (patches) => patches.some((p) => p.status && p.status !== "ACTIVE"),
      storageKey: "user_deactivate_skip_confirmation",
      renderModal: ({ patches, onConfirm, onCancel }) => {
        const deactivatePatches = patches.filter((p) => p.status && p.status !== "ACTIVE");
        return (
          <DeactivateUserModal
            count={deactivatePatches.length}
            onConfirm={async (dontShowAgain) => {
              if (dontShowAgain) {
                await AsyncStorage.setItem("user_deactivate_skip_confirmation", "true");
              }
              onConfirm();
            }}
            onCancel={onCancel}
          />
        );
      },
    }),
    [],
  );

  // Delete confirmation for deleting users
  const deleteConfirmation: DeleteConfirmationConfig<UserType> = useMemo(
    () => ({
      storageKey: "user_delete_skip_confirmation",
      renderModal: ({ count, onConfirm, onCancel }) => (
        <DeleteUserModal
          count={count}
          onConfirm={async (dontShowAgain) => {
            if (dontShowAgain) {
              await AsyncStorage.setItem("user_delete_skip_confirmation", "true");
            }
            onConfirm();
          }}
          onCancel={onCancel}
        />
      ),
    }),
    [],
  );

  if (!hasUsersAccess) {
    return <NoPermissionScreen entityName={displayNames?.users?.plural || "Users"} />;
  }

  return (
    <>
      <Link asChild href="/users/user/ed886694-948a-52f1-8c38-61bd9afe682e"></Link>
      <EntityTablePage<UserType>
        {...tableConfig}
        fetchData={userService.getUsers}
        updateEntities={userService.saveRowPatches}
        deleteEntities={userService.deleteUsersByIds}
        saveConfirmation={saveConfirmation}
        deleteConfirmation={deleteConfirmation}
      />
    </>
  );
}
